/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { OverlayView, OverlayViewF } from '@react-google-maps/api'
import { t } from 'i18next'
import { useEffect, useMemo, useState, type Dispatch } from 'react'
import { FavoriteEvent } from '../favorite-event/favorite-event'
import { Icon } from '../icon/icon'
import Text from '../text/text'
import { getPixelPositionWindow } from './map.helper'
import { NavLink as RemixNavLink } from 'react-router'
import { getImgSrc } from '~/utils/image'
import { cn } from '~/utils/misc'
import { trackClick } from '~/utils/statistics/statistics.helper'
import { useOptionalUser } from '~/utils/user'
import type { EventIndex } from '~/types/eventIndex'

export default function MapWindow({
	events,
	map,
	toggleModal,
	onClose,
}: {
	events: EventIndex[]
	map: google.maps.Map
	toggleModal: () => void
	onClose: Dispatch<any>
}) {
	const [index, setIndex] = useState(0)
	const [event, setEvent] = useState<EventIndex>(events[0])
	const user = useOptionalUser()
	const [favorite, setFavorite] = useState(
		user?.favorites.some(e => e.id === events[0].dateId) ?? false,
	)

	useEffect(() => {
		setFavorite(
			user?.favorites.some(e => e.id === events[index].dateId) ?? false,
		)
	}, [index, user?.favorites])

	const changeEvent = (direction: 'next' | 'prev') => {
		if (direction === 'next') {
			if (index + 1 < events.length) {
				setIndex(index + 1)
				setEvent(events[index + 1])
			} else {
				setIndex(0)
				setEvent(events[0])
			}
		} else {
			if (index - 1 >= 0) {
				setIndex(index - 1)
				setEvent(events[index - 1])
			} else {
				setIndex(events.length - 1)
				setEvent(events[events.length - 1])
			}
		}
	}

	useEffect(() => {
		setIndex(0)
		setEvent(events[0])
		setShortestPosition(shortestPositionFunc())
	}, [events])

	const shortestPositionFunc = () => {
		const bounds = map.getBounds()
		const ne = bounds!.getNorthEast()
		const sw = bounds!.getSouthWest()
		const latLng = {
			lat: () => event.latitude,
			lng: () => event.longitude,
		}
		const lat = latLng!.lat()
		const lng = latLng!.lng()

		const distanceFromTop = ne.lat() - lat
		const distanceFromRight = ne.lng() - lng
		const distanceFromBottom = lat - sw.lat()
		const distanceFromLeft = lng - sw.lng()

		const shortestDistance = Math.min(
			distanceFromTop,
			distanceFromRight,
			distanceFromBottom,
			distanceFromLeft,
		)
		let shortestPosition: 'top' | 'bottom' | 'left' | 'right' = 'top'
		switch (shortestDistance) {
			case distanceFromRight:
				shortestPosition = 'right'
				break
			case distanceFromBottom:
				shortestPosition = 'bottom'
				break
			case distanceFromLeft:
				shortestPosition = 'left'
				break
			default:
				shortestPosition = 'top'
		}
		return shortestPosition
	}

	const [shortestPosition, setShortestPosition] = useState<
		'top' | 'bottom' | 'left' | 'right'
	>(shortestPositionFunc())

	const memoMarker = useMemo(() => {
		const pointerPositionClass =
			shortestPosition === 'top'
				? '-top-4 left-1/2 mt-2 -translate-x-1/2'
				: shortestPosition === 'bottom'
					? '-bottom-4 left-1/2 mb-2 -translate-x-1/2'
					: shortestPosition === 'left'
						? '-left-4 top-1/2 ml-2 -translate-y-1/2'
						: '-right-4 top-1/2 mr-2 -translate-y-1/2'

		return window.innerWidth > 768 ? (
			<OverlayViewF
				position={{
					lat: event.latitude,
					lng: event.longitude,
				}}
				mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
				getPixelPositionOffset={(width, height) =>
					getPixelPositionWindow(width, height, shortestPosition)
				}
				zIndex={100}
			>
				<div className="relative w-[300px] select-none rounded-xl border-t bg-background text-foreground shadow-lg">
					<div
						className={cn(
							'absolute h-4 w-4 rotate-45 transform bg-background shadow-lg',
							pointerPositionClass,
						)}
					></div>
					<button
						onClick={() => onClose(null)}
						className="absolute left-0 top-0 z-20 m-2 h-6 w-6 justify-center rounded-full bg-background text-center align-middle text-foreground opacity-70 shadow-lg duration-200 hover:opacity-100"
					>
						<Icon name="cross" size="xs" />
					</button>
					{user ? (
						<div className="absolute right-6 top-0 z-20 h-6 w-6 justify-center text-center">
							<FavoriteEvent
								eventDateId={event.dateId}
								eventName={event.title}
								favoredByUser={favorite}
							/>
						</div>
					) : (
						<div className="absolute right-3 top-2 z-20 transition duration-200 hover:scale-105">
							<div>
								<button
									aria-label={t('action_add_as_favorite', {
										title: event.title,
									})}
									className="items-center justify-center text-foreground/40 dark:text-background/50"
									onClick={toggleModal}
								>
									<Icon
										className="stoke-1 scale-125 stroke-white max-md:scale-150"
										name="heart-2"
										size="md"
									/>
								</button>
							</div>
						</div>
					)}
					<RemixNavLink
						to={event.url}
						className="relative z-10"
						state={{ from: 'internal' }}
						onClick={() => trackClick(event.dateId, 'map')}
					>
						<img
							className="h-[200px] w-full overflow-hidden rounded-t-xl object-cover"
							src={getImgSrc(event?.imageId, 'event')}
							alt={event.title}
							loading="lazy"
						/>
						<div className="px-4 py-4">
							<div className="mb-2 truncate text-base font-semibold">
								{event.title}
							</div>
							{event.location && (
								<Text as="sm" className="flex items-center py-1">
									<Icon
										name="position-marker"
										className="mr-2 text-lg text-secondary"
									/>
									{event.location}
								</Text>
							)}
							{event.dateTime && (
								<Text as="sm" className="flex items-center py-1">
									<Icon
										name="calendar"
										className="mr-2 text-lg text-secondary"
									/>
									{event.dateTime}
								</Text>
							)}
						</div>
					</RemixNavLink>
					{events.length > 1 && (
						<div className="flex justify-between border-t align-middle">
							<div
								className="cursor-pointer p-2 px-4"
								onClick={() => changeEvent('prev')}
							>
								<Icon
									name="chevron-down"
									className="rotate-90 drop-shadow-xl"
									size="md"
								/>
							</div>

							<Text as="sm" className="self-center">
								{index + 1} / {events.length}
							</Text>
							<div
								className="cursor-pointer p-2 px-4"
								onClick={() => changeEvent('next')}
							>
								<Icon
									name="chevron-down"
									className="-rotate-90 drop-shadow-xl"
									size="md"
								/>
							</div>
						</div>
					)}
				</div>
			</OverlayViewF>
		) : (
			<div className="absolute bottom-3 left-3 right-3 z-20 animate-fade select-none rounded-xl border-t bg-background text-foreground">
				<button
					onClick={() => onClose(null)}
					className="absolute left-0 top-0 z-20 m-2 h-8 w-8 items-center justify-center rounded-full bg-background text-center align-middle text-foreground opacity-70 shadow-lg duration-200 hover:opacity-100"
				>
					<Icon name="cross" size="xs" />
				</button>
				{user && (
					<button className="absolute right-4 top-0 z-20 h-8 w-8 justify-center text-center">
						<FavoriteEvent
							eventDateId={event.dateId}
							eventName={event.title}
							favoredByUser={favorite}
						/>
					</button>
				)}
				<RemixNavLink
					to={event.url}
					className="relative z-10 flex h-[150px] flex-row items-center"
					state={{ from: 'internal' }}
					onClick={() => trackClick(event.dateId, 'map')}
				>
					<img
						className="aspect-square h-[150px] shrink-0 overflow-hidden rounded-l-xl object-cover"
						src={getImgSrc(event?.imageId, 'event')}
						alt={event.title}
						loading="lazy"
					/>
					<div className="grow overflow-hidden px-4 py-4">
						<div className="mb-2 truncate pr-6 text-base font-semibold">
							{event.title}
						</div>
						{event.location && (
							<Text as="sm" className="flex items-center py-1">
								<Icon
									name="position-marker"
									className="mr-2 text-lg text-secondary"
								/>
								{event.location}
							</Text>
						)}
						{event.dateTime && (
							<Text as="sm" className="flex items-center py-1">
								<Icon name="calendar" className="mr-2 text-lg text-secondary" />
								{event.dateTime}
							</Text>
						)}
					</div>
				</RemixNavLink>
				{events.length > 1 && (
					<div className="flex justify-between border-t align-middle">
						<div
							className="cursor-pointer p-4 px-6"
							onClick={() => changeEvent('prev')}
						>
							<Icon
								name="chevron-down"
								className="rotate-90 drop-shadow-xl"
								size="lg"
							/>
						</div>

						<Text className="self-center">
							{index + 1} / {events.length}
						</Text>
						<div
							className="cursor-pointer p-4 px-6"
							onClick={() => changeEvent('next')}
						>
							<Icon
								name="chevron-down"
								className="-rotate-90 drop-shadow-xl"
								size="lg"
							/>
						</div>
					</div>
				)}
			</div>
		)
	}, [index, event, favorite, onClose])
	return memoMarker
}
